import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputBase from '@mui/material/InputBase';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getSurfaceBorder,
  getSurfaceColor,
  getThemeTokens,
  radii,
} from '../../../theme/tokens';
import { useHomeIdentity } from '../hooks/useHomeIdentity';

const getGreeting = (): string => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return 'Good morning';
  if (hour >= 12 && hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const formatDate = (): string => {
  const now = new Date();
  const weekday = now.toLocaleDateString('en-US', { weekday: 'long' });
  const day = now.getDate();
  const month = now.toLocaleDateString('en-US', { month: 'long' });
  return `${weekday} ${day} ${month}`;
};

export const GreetingBanner = () => {
  const theme = useTheme();
  const tokens = getThemeTokens(theme);
  const { value: identity, loading } = useHomeIdentity();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const handleSearch = () => {
    if (query.trim()) {
      navigate(`/search?query=${encodeURIComponent(query.trim())}`);
    }
  };

  return (
    <Box
      sx={{
        background:
          theme.palette.mode === 'light'
            ? `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 100%)`
            : `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 100%)`,
        borderRadius: `${radii.xl}px`,
        mx: -3,
        px: 3,
        py: 5,
        textAlign: 'center',
      }}
    >
      <Typography
        sx={{ color: tokens.textMuted, mb: 0.5, letterSpacing: '0.02em' }}
        variant="body2"
      >
        {formatDate()}
      </Typography>
      <Typography variant="h3" sx={{ fontWeight: 600 }}>
        {loading ? (
          <Skeleton width={280} sx={{ mx: 'auto' }} />
        ) : (
          <>
            {`${getGreeting()}, `}
            <Box component="span" sx={{ color: 'primary.main' }}>
              {identity?.firstName ?? 'there'}
            </Box>
            .
          </>
        )}
      </Typography>
      <Typography
        color="text.secondary"
        sx={{ mt: 0.75, mb: 3 }}
        variant="body2"
      >
        Search services, APIs, tech docs, and teams.
      </Typography>
      <Box
        component="form"
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault();
          handleSearch();
        }}
        sx={{
          backgroundColor: getSurfaceColor(theme, 'raised'),
          border: `1px solid ${getSurfaceBorder(theme)}`,
          borderRadius: `${radii.lg}px`,
          display: 'flex',
          alignItems: 'center',
          maxWidth: 720,
          mx: 'auto',
          px: 2.5,
          py: 0.25,
          transition: 'border-color 150ms ease, box-shadow 150ms ease',
          '&:focus-within': {
            borderColor: theme.palette.primary.main,
            boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.12)}`,
          },
        }}
      >
        <SearchIcon sx={{ color: tokens.textMuted, mr: 1.5, fontSize: 22 }} />
        <InputBase
          placeholder="Search the portal..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          sx={{
            flex: 1,
            fontSize: '0.95rem',
            py: 1,
          }}
          inputProps={{ 'aria-label': 'Search the portal' }}
        />
        <Button
          type="submit"
          variant="contained"
          size="small"
          disableElevation
          sx={{
            borderRadius: `${radii.sm}px`,
            px: 2,
            py: 0.75,
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Search
        </Button>
      </Box>
    </Box>
  );
};
